import clsx from 'clsx';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';

import { setCustomParticipantVisible } from '../../../base/conference';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { observeTile, unobserveTile } from '../../../base/lastn/onScreenTiles';
import { VIDEO_TYPE } from '../../../base/media';
import {
    getParticipantById,
    getPinnedParticipant,
    pinParticipant
} from '../../../base/participants';
import { connect } from '../../../base/redux';
import { exitTileViewIfNeeded } from '../../../conference/filmStripViewManager';
import { LAYOUTS, VideoLayout } from '../../../video-layout';
import { getRemoteVideoZoom } from '../../../zoom-sync';
import {
    handleThumbnailClick,
    hideActiveDoubleClickTooltip,
    updateThumbnailZoomTransform
} from '../../functions.web';

import { RemoteVideoContextMenu, showRemoteVideoContextMenu } from './RemoteVideoContextMenu';
import Thumbnail from './Thumbnail';

type Props = {

    /**
     * The current filmstrip view.
     */
    _filmStripView?: string,

    /**
     * Whether audio-only mode is active.
     */
    _isAudioOnly: boolean,

    /**
     * Whether picture-in-picture mode is enabled.
     */
    _isPiP: boolean,

    /**
     * The object fit used for the video in tile view.
     */
    _objectFit: string,

    /**
     * The participant object from redux.
     */
    _participant?: Object,

    /**
     * The pinned participant from redux.
     */
    _pinnedParticipant?: Object,

    /**
     * The synchronized zoom of this participant's camera.
     */
    _zoomState?: Object,

    /**
     * Additional CSS class name.
     */
    className?: string,

    /**
     * The redux dispatch function.
     */
    dispatch: Function,

    /**
     * Whether the thumbnail is hovered (controlled).
     */
    isHovered?: boolean,

    /**
     * The ID of the participant.
     */
    participantID: string,

    /**
     * Custom inline styles.
     */
    style?: Object
};

type State = {
    isHovered: boolean
};

/**
 * React component representing a remote participant thumbnail.
 */
class RemoteVideo extends Component<Props, State> {
    _containerRef: Object;
    _firstClick: ?number;

    /**
     * Initializes a new RemoteVideo component instance.
     *
     * @param {Props} props - The component props.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            isHovered: false
        };

        this._containerRef = React.createRef();

        this._onMouseEnter = this._onMouseEnter.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
        this._onContextMenu = this._onContextMenu.bind(this);
        this._onClick = this._onClick.bind(this);
        this._setFirstClick = this._setFirstClick.bind(this);
        this.togglePin = this.togglePin.bind(this);
        this.updateZoomTransform = this.updateZoomTransform.bind(this);
    }

    /**
     * Registers the thumbnail with VideoLayout and syncs zoom transform.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._register(this.props.participantID);
        this.updateZoomTransform();
    }

    /**
     * Re-registers on participant change and re-applies zoom when it changes.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (prevProps.participantID !== this.props.participantID) {
            this._unregister(prevProps.participantID);
            this._register(this.props.participantID);
        }

        if (prevProps.participantID !== this.props.participantID
                || prevProps._zoomState !== this.props._zoomState
                || prevProps._filmStripView !== this.props._filmStripView
                || prevProps._objectFit !== this.props._objectFit) {
            this.updateZoomTransform();
        }
    }

    /**
     * Unregisters the thumbnail and clears the double click tooltip.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._clearDoubleClickTimeout();
        this._unregister(this.props.participantID);
    }

    /**
     * Returns the participant ID; used by VideoLayout.
     *
     * @returns {string}
     */
    getId(): string {
        return this.props.participantID;
    }

    /**
     * Pins this participant, or unpins if already pinned; used by VideoLayout.
     *
     * @returns {void}
     */
    togglePin(): void {
        const { _filmStripView, _pinnedParticipant, dispatch, participantID } = this.props;

        exitTileViewIfNeeded(dispatch, _filmStripView);
        dispatch(pinParticipant(_pinnedParticipant?.id === participantID ? null : participantID));
        dispatch(setCustomParticipantVisible(false));
    }

    /**
     * Applies the synchronized zoom to the thumbnail video.
     *
     * @returns {void}
     */
    updateZoomTransform(): void {
        updateThumbnailZoomTransform({
            container: this._containerRef.current,
            isLocal: false,
            objectFit: this._getObjectFit(),
            zoomState: this.props._zoomState
        });
    }

    /**
     * Returns the object fit of the video for the current view.
     *
     * @private
     * @returns {string}
     */
    _getObjectFit() {
        return this.props._filmStripView === LAYOUTS.TILE_VIEW ? this.props._objectFit : 'cover';
    }

    /**
     * Registers the thumbnail with VideoLayout and the on-screen tile tracker.
     *
     * @param {string} id - The participant ID.
     * @private
     * @returns {void}
     */
    _register(id) {
        if (id) {
            VideoLayout.addRemoteVideoContainer(id, this);
            observeTile(id, this._containerRef.current);
        }
    }

    /**
     * Unregisters the thumbnail from VideoLayout and the on-screen tile tracker.
     *
     * @param {string} id - The participant ID.
     * @private
     * @returns {void}
     */
    _unregister(id) {
        if (id) {
            VideoLayout.unregisterRemoteVideoContainer(id);
            unobserveTile(id);
        }
    }

    /**
     * Handles mouse enter event.
     *
     * @private
     * @returns {void}
     */
    _onMouseEnter() {
        this.setState({ isHovered: true });
    }

    /**
     * Handles mouse leave event.
     *
     * @private
     * @returns {void}
     */
    _onMouseLeave() {
        this.setState({ isHovered: false });
        this._clearDoubleClickTimeout();
    }

    /**
     * Clears any active double click timer and tooltip.
     *
     * @private
     * @returns {void}
     */
    _clearDoubleClickTimeout() {
        if (this._firstClick) {
            window.clearTimeout(this._firstClick);
            this._firstClick = undefined;
        }
        hideActiveDoubleClickTooltip();
    }

    /**
     * Stores the double click timer.
     *
     * @param {?number} timer - The timer.
     * @private
     * @returns {void}
     */
    _setFirstClick(timer) {
        this._firstClick = timer;
    }

    /**
     * Handles context menu event.
     *
     * @param {React.MouseEvent} event - The context menu event.
     * @private
     * @returns {void}
     */
    _onContextMenu(event: React.MouseEvent) {
        showRemoteVideoContextMenu(event.nativeEvent, `participant_${this.props.participantID}`);
    }

    /**
     * Handles click event for pinning/unpinning.
     *
     * @param {React.MouseEvent} event - The click event.
     * @private
     * @returns {void}
     */
    _onClick(event: React.MouseEvent) {
        handleThumbnailClick({
            container: this._containerRef.current,
            dispatch: this.props.dispatch,
            event,
            filmStripView: this.props._filmStripView,
            firstClickTimer: this._firstClick,
            id: this.props.participantID,
            isPiP: this.props._isPiP,
            pinnedParticipant: this.props._pinnedParticipant,
            setFirstClickTimer: this._setFirstClick
        });
    }

    /**
     * Implements React's Component.render().
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const {
            _isAudioOnly,
            _participant,
            className,
            isHovered: controlledHover,
            participantID: id,
            style
        } = this.props;

        if (!id) {
            return null;
        }

        const isHovered = typeof controlledHover === 'boolean' ? controlledHover : this.state.isHovered;
        const isPinned = Boolean(_participant?.pinned || _participant?.moderatorPin);
        const videoSpanId = `participant_${id}`;
        const portalContainer = document.getElementById('context-menu-portal');
        const contextMenu = (
            <RemoteVideoContextMenu
                participantId = { id }
                showParticipantName = { true }
                videoSpanId = { videoSpanId } />
        );

        return (
            <span
                className = { clsx(
                    'videocontainer',
                    _isAudioOnly && 'audio-only',
                    isPinned && 'videoContainerFocused',
                    className
                ) }
                id = { videoSpanId }
                onClick = { this._onClick }
                onContextMenu = { this._onContextMenu }
                onMouseEnter = { this._onMouseEnter }
                onMouseLeave = { this._onMouseLeave }
                ref = { this._containerRef }
                style = {{
                    objectFit: this._getObjectFit(),
                    ...style
                }}>
                <Thumbnail
                    isHovered = { isHovered }
                    participantID = { id } />
                {portalContainer ? createPortal(contextMenu, portalContainer) : contextMenu}
            </span>
        );
    }
}

/**
 * Maps (parts of) the redux state to the associated RemoteVideo props.
 *
 * @param {Object} state - The redux state.
 * @param {Props} ownProps - The own props of the component.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps: Props) {
    const { participantID } = ownProps;
    const conference = state['features/base/conference'];

    return {
        _filmStripView: conference?.filmStripView,
        _isAudioOnly: Boolean(conference?.audioOnly),
        _isPiP: Boolean(getPictureInPictureModeConfig(state)),
        _objectFit: conference?.objectFit || 'cover',
        _participant: getParticipantById(state, participantID),
        _pinnedParticipant: getPinnedParticipant(state),
        _zoomState: getRemoteVideoZoom(state, participantID, VIDEO_TYPE.CAMERA)
    };
}

export default connect(_mapStateToProps)(RemoteVideo);
